import { Component, Vue } from 'vue-property-decorator';
import { PlatformRole, ProjectRole } from 'ignite360-core';

@Component
export class AuthMixin extends Vue {
  get user() {
    return this.$store.state.auth.user;
  }

  get currentProjectId(): string {
    return this.$route.fullPath.startsWith('/projects/') ? this.$route.params.id : '';
  }

  get currentProjectRole(): ProjectRole | undefined {
    if (!this.user || !this.currentProjectId) {
      return undefined;
    }
    return this.user.botRoles?.[this.currentProjectId];
  }

  /**
   * Hierarchical project permission check (Admin < Editor < AnalyticsViewer).
   * AccountManager is not part of this ladder.
   */
  hasPermission(role: ProjectRole): boolean {
    const botId = this.$route.params.id;

    if (!this.user) {
      return false;
    }
    if (this.hasSuperAdminRole()) {
      return true;
    }

    const projectRole = this.user.botRoles?.[botId];
    if (projectRole === undefined || projectRole === null) {
      return false;
    }
    if (projectRole === ProjectRole.AccountManager) {
      return false;
    }

    return projectRole <= role;
  }

  hasSuperAdminRole(): boolean {
    return this.user?.role === PlatformRole.SuperAdmin && !this.isImpersonating;
  }

  get isImpersonating(): boolean {
    return !!this.user?.isImpersonating;
  }

  hasAccountDirectorRole(): boolean {
    return this.user?.role === PlatformRole.AccountDirector;
  }

  canCreateProject(): boolean {
    return this.hasSuperAdminRole() || this.hasAccountDirectorRole();
  }

  canEditProjectSettings(): boolean {
    if (this.hasSuperAdminRole()) {
      return true;
    }
    if (!this.hasAccountDirectorRole()) {
      return false;
    }
    // Create modal has no project id yet; otherwise require Admin on the project
    if (!this.currentProjectId) {
      return true;
    }
    return this.currentProjectRole === ProjectRole.Admin;
  }

  canPublishPosts(): boolean {
    if (this.hasSuperAdminRole()) {
      return true;
    }
    if (this.currentProjectRole === ProjectRole.AccountManager) {
      return true;
    }
    return this.hasPermission(ProjectRole.Editor);
  }

  canViewOverview(): boolean {
    if (this.hasSuperAdminRole()) {
      return true;
    }
    return this.currentProjectRole !== undefined;
  }

  hasEditorRole(): boolean {
    return this.hasPermission(ProjectRole.Editor);
  }

  hasAnalyticsViewerRole(): boolean {
    return this.hasPermission(ProjectRole.AnalyticsViewer);
  }

  hasAdminRole(): boolean {
    return this.currentProjectRole === ProjectRole.Admin || this.hasSuperAdminRole();
  }

  hasAccountManagerRole(): boolean {
    return this.currentProjectRole === ProjectRole.AccountManager || this.hasSuperAdminRole();
  }
}
